import { DataProgress, DataValidation, ReportStep } from "@/models/graphql-global-types";
import { getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { store } from "@/store/store";
import moment, { Moment } from "moment";
import { ReportOverview } from "@/models/queries/ReportOverviewQuery";
import { NewReportStore } from "./NewReportStore";
import { sortReportStep } from "@/utils/sort";
import { UserService } from "@/services/UserService";
import * as _ from "lodash";

export enum TimeFrame {
  WEEK = "WEEK",
  MONTH = "MONTH",
  YEAR = "YEAR"
}

@Module({ dynamic: true, store, name: "reportFilterStore" })
class ReportFilter extends VuexModule {
  public filteredReports: ReportOverview[] = [];
  public reportSteps: ReportStep[] = [];
  public timeFrame: TimeFrame | null = null;
  public progress: DataProgress[] = [];
  public dataValidations: DataValidation[] = [];
  public customer: string | null = null;

  @Mutation
  public setDefaultReportSteps() {
    let defaultSteps: ReportStep[] = [];
    if (UserService.isUserAdmin()) {
      defaultSteps = [];
    } else if (UserService.isUserSupervisor()) {
      defaultSteps = [ReportStep.DONE_GATHERING, ReportStep.COMPLETED];
    } else if (UserService.isUserTester()) {
      defaultSteps = [];
    } else if (UserService.isUserViewer()) {
      defaultSteps = [ReportStep.COMPLETED];
    }
    this.reportSteps = defaultSteps;
  }

  @Mutation
  public clearReportFilter() {
    this.filteredReports = [];
    this.reportSteps = [];
    this.timeFrame = null;
    this.progress = [];
    this.dataValidations = [];
    this.customer = null;
  }

  @Mutation
  public setTimeFrame(timeFrame: TimeFrame) {
    this.timeFrame = this.timeFrame === timeFrame ? null : timeFrame;
    ReportFilterStore.applyFilter();
  }

  @Mutation
  public setCustomer(customer: string) {
    this.customer = this.customer === customer ? null : customer;
    ReportFilterStore.applyFilter();
  }

  @Mutation
  public setProgress(progress: DataProgress) {
    const index = this.progress.indexOf(progress);
    if (index >= 0) {
      this.progress.splice(index, 1);
    } else {
      this.progress.push(progress);
    }
    ReportFilterStore.applyFilter();
  }

  @Mutation
  public setReportStep(reportStep: ReportStep) {
    const index = this.reportSteps.indexOf(reportStep);
    if (index >= 0) {
      this.reportSteps.splice(index, 1);
    } else {
      this.reportSteps.push(reportStep);
    }
    ReportFilterStore.applyFilter();
  }

  @Mutation
  public setDataValidation(dataValidation: DataValidation) {
    const index = this.dataValidations.indexOf(dataValidation);
    if (index >= 0) {
      this.dataValidations.splice(index, 1);
    } else {
      this.dataValidations.push(dataValidation);
    }
    ReportFilterStore.applyFilter();
  }

  @Mutation
  public applyFilter() {
    let filtered = NewReportStore.reports;
    if (this.reportSteps.length > 0) {
      filtered = filtered.filter(r => this.reportSteps.includes(r.step));
    }
    if (this.timeFrame) {
      let date: Moment = moment();

      switch (this.timeFrame.valueOf()) {
        case TimeFrame.WEEK:
          date = moment()
            .subtract(7, "days")
            .startOf("day");
          break;
        case TimeFrame.MONTH:
          date = moment()
            .subtract(1, "months")
            .startOf("day");
          break;
        case TimeFrame.YEAR:
          date = moment()
            .subtract(1, "years")
            .startOf("day");
          break;
      }

      filtered = filtered.filter(r => {
        const findDate = UserService.isUserOwnerOfReport(r) ? r.created_at : r.report_complete;
        return findDate && moment(findDate).isAfter(date);
      });
    }
    if (this.progress.length > 0) {
      filtered = filtered.filter(r => this.progress.includes(r.dataProgress));
    }
    if (this.dataValidations.length > 0) {
      filtered = filtered.filter(r => {
        if (this.dataValidations.includes(DataValidation.TESTED_OK)) {
          // TODO: ADD EXCEPTION FOR EMPTY DATA VALIDATION
          return (
            (r.dataValidation && this.dataValidations.includes(r.dataValidation)) ||
            r.dataValidation === DataValidation.NOT_TESTED ||
            !r.dataValidation
          );
        }
        return r.dataValidation && this.dataValidations.includes(r.dataValidation);
      });
    }

    if (this.customer) {
      filtered = filtered.filter(r => r.sheets?.find(s => s.product.customer === this.customer));
    }

    filtered.forEach(report => {
      const numberParts = report.report_number.split("-"); // DEN-19-00017
      report.splittedReportNumber = {
        c: numberParts[0],
        y: numberParts[1],
        n: numberParts[2]
      };
    });

    this.filteredReports = _.orderBy(
      filtered,
      ["splittedReportNumber.c", "splittedReportNumber.y", "splittedReportNumber.n"],
      ["asc", "desc", "desc"]
    );
  }
}

export const ReportFilterStore = getModule(ReportFilter);
